/* eslint-disable @typescript-eslint/no-explicit-any */
import { AxiosError } from 'axios';
import {
  CreateEntityTypeRequest,
  EntityType,
  FetchEntityTypesQueryParams,
} from '../models/EntityType';
import { HTTPClient } from './httpClient';
const http = new HTTPClient(import.meta.env.VITE_ENTITY_MANAGEMENT_API);

interface EntityTypesFetchResponse {
  _embedded: {
    items: EntityType[];
  };
}

/**
 * Fetches all entity type for tenant and given query params
 * @param tenant - tenantId
 * @param qParams - contains offset and sort order
 * @returns - entity types
 */
export async function fetchEntityTypes(
  tenant: string,
  qParams: FetchEntityTypesQueryParams,
): Promise<EntityType[]> {
  const suffixUrl = `/v1/tenants/${tenant}/entityTypes`;
  try {
    const params = setQueryParams(qParams);
    const resp = await http.get<EntityTypesFetchResponse>(suffixUrl, params);
    return resp._embedded.items;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error(error.message);
    }
    throw error;
  }
}

/**
 * gets entity type by id
 * @param tenant - tenantId
 * @param entityTypeId - entity type id
 * @returns - entity type
 */
export async function getEntityType(
  tenant: string,
  entityTypeId: string,
): Promise<EntityType | null> {
  try {
    const url = `/v1/tenants/${tenant}/entityTypes/${entityTypeId}`;
    const resp = await http.get(url, undefined);
    return resp;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error(error.message);
    }
    throw error;
  }
}

/**
 * Creates a new entity type for a tenant
 * @param tenant - tenantId
 * @param body - entity type data to create
 * @returns - created entity type
 */
export async function createEntityType(
  tenant: string,
  body: CreateEntityTypeRequest,
): Promise<EntityType> {
  const url = `/v1/tenants/${tenant}/entityTypes`;
  try {
    const resp = await http.post<EntityType>(url, body);
    return resp;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error(error.message);
    }
    throw error;
  }
}

function setQueryParams(qParams: Record<string, any>) {
  const params = new URLSearchParams();
  Object.entries(qParams).forEach(([key, value]) => {
    if (Array.isArray(value) && value.length > 0) {
      // Append each value in the array if it has elements
      value.forEach((val) => params.append(key, val.toString()));
    } else if (
      value !== undefined &&
      value !== null &&
      value.toString().trim() !== ''
    ) {
      // Append single non-array value if it exists and is not empty
      params.append(key, value.toString());
    }
  });
  return params;
}

export async function patchEntityType(
  tenant: string,
  entityTypeId: string,
  body: CreateEntityTypeRequest,
  version: string,
): Promise<EntityType | null> {
  try {
    const url = `/v1/tenants/${tenant}/entityTypes/${entityTypeId}/versions/${version}`;
    const resp = await http.patch(url, {
      description: body.description,
      tags: body.tags,
    });
    return resp;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error(error.message);
    }
    throw error;
  }
}

export async function putEntityType(
  tenant: string,
  entityTypeId: string,
  body: CreateEntityTypeRequest,
): Promise<EntityType | null> {
  try {
    const url = `/v1/tenants/${tenant}/entityTypes/${entityTypeId}`;
    const resp = await http.put(url, body);

    return resp;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error(error.message);
    }
    throw error;
  }
}

export async function putEntityTypeRelationships(
  tenant: string,
  entityTypeId: string,
  entityTypeRelationships: any,
) {
  try {
    const url = `/v1/tenants/${tenant}/entityTypes/${entityTypeId}/relationships`;
    const resp = await http.put(url, entityTypeRelationships);

    return resp;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error(error.message);
    }
    throw error;
  }
}
